import type { Song } from '@/storage/types';

export type SongSort = 'recent' | 'title' | 'artist' | 'key' | 'bpm' | 'duration';

export const SORT_OPTIONS: { value: SongSort; label: string }[] = [
  { value: 'recent', label: 'Last opened' },
  { value: 'title', label: 'Title' },
  { value: 'artist', label: 'Artist' },
  { value: 'key', label: 'Key' },
  { value: 'bpm', label: 'BPM' },
  { value: 'duration', label: 'Length' },
];

const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

/** Case- and accent-insensitive match on title, artist and file name (F7.3). */
export function matchesSearch(song: Song, query: string): boolean {
  const q = query.trim().toLocaleLowerCase();
  if (!q) return true;
  return [song.title, song.artist ?? '', song.fileName].some((field) =>
    field
      .toLocaleLowerCase()
      .normalize('NFD')
      .replace(/\p{M}/gu, '')
      .includes(q.normalize('NFD').replace(/\p{M}/gu, '')),
  );
}

function compare(a: Song, b: Song, sort: SongSort): number {
  switch (sort) {
    case 'recent':
      return b.lastOpenedAt - a.lastOpenedAt;
    case 'title':
      return collator.compare(a.title, b.title);
    case 'artist':
      // Songs without an artist go last.
      if (!a.artist !== !b.artist) return a.artist ? -1 : 1;
      return collator.compare(a.artist ?? '', b.artist ?? '') || collator.compare(a.title, b.title);
    case 'key':
      return (
        a.summary.key.tonic - b.summary.key.tonic ||
        a.summary.key.mode.localeCompare(b.summary.key.mode)
      );
    case 'bpm':
      return a.summary.bpm - b.summary.bpm;
    case 'duration':
      return a.duration - b.duration;
  }
}

export function querySongs(songs: Song[], search: string, sort: SongSort): Song[] {
  return songs.filter((s) => matchesSearch(s, search)).sort((a, b) => compare(a, b, sort));
}
